/* eslint-disable import/no-unresolved,no-param-reassign */
import { sortBy } from 'lodash';
import uuid from 'react-uuid';
import { SECURITY_REFERENCE_ALIAS } from 'common/constants/cap-table';
import { SAFE_DECIMAL_PLACES } from 'common/constants/general';
import { Format } from 'common/types/format';
import { GridType } from 'common/types/scalarSpreadsheet';
import { CellValue } from 'models/interfaces/spreadsheet-types/core-types';
import { parseDatabaseValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { VARIABLE_VALUE } from './variablePropertiesUtils';
import { CapTableCell, CaptableColumn } from '../components/captable-table/CaptableTable.props';
import rowConfig, { RowConfigProps } from '../data/rowConfig';

const reverseParser = ({
  cells,
  columns,
  fieldAttributes = {},
  allowEmptyValues = true,
}: {
  cells: Record<string, CapTableCell>;
  columns: CaptableColumn[];
  fieldAttributes: Record<string, any>;
  allowEmptyValues?: boolean;
  }) => {
  const sortedColumns = sortBy(columns, ['order']);
  const localConfig = rowConfig(sortedColumns as unknown as RowConfigProps);
  const alphabet = alphabetGenerator([], sortedColumns.length);

  function parseValue(
    cell: CapTableCell,
    key: string,
    column: CaptableColumn,
    type: string,
    value: CellValue,
    defaultValue: CellValue,
    format: Format,
    gridType: GridType,
    decimalPlaces: number
  ) {
    if (cell.alias && cells[key]) {
      const cellAllowEmptyValues = cell.allowEmptyValues || allowEmptyValues;
      column.columnRef = column.security_ref!;
      const currentAlias = cell.alias;
      column[currentAlias] = parseDatabaseValue({
        type,
        value,
        format,
        allowEmptyValues: cellAllowEmptyValues,
        gridType,
        decimalPlaces,
      });
    }
  }

  sortedColumns.forEach((column, columnIndex) => {
    const columnLegend = alphabet[columnIndex];

    column.order = columnIndex;

    // Collect all mapping references for this column (security)
    const columnMappingReferences: any[] = [];

    // replace updated values
    (localConfig as CapTableCell[]).forEach((cell: CapTableCell, cellIndex: number) => {
      if (!cell.totalKey) {
        const key = columnLegend + (cellIndex + 1);
        const defaultValue = (cell.defaultValue || null) as CellValue;
        const type = cell.dbType || null;
        const format = (cell.format || null) as Format;
        let value = (cells[key] ? cells[key].value : defaultValue) as string | null;
        const gridType = cell?.gridType as GridType;

        if (cell.alias === SECURITY_REFERENCE_ALIAS) {
          value = value || uuid();
        }

        if (value === VARIABLE_VALUE) {
          value = null;
        }

        const { alias: rowAlias } = cell;
        const decimalPlaces = rowAlias ? fieldAttributes[rowAlias]?.decimal_places : SAFE_DECIMAL_PLACES;

        parseValue(cell, key, column, type!, value, defaultValue, format, gridType, decimalPlaces);

        // Extract mapping references from the cell if they exist
        const cellWithRefs = cells[key] as any;
        if (cellWithRefs?.mappingReferences && Array.isArray(cellWithRefs.mappingReferences)) {
          columnMappingReferences.push(...cellWithRefs.mappingReferences);
        }
      }
    });

    // Always attach mapping references to the security (column), even if empty
    // This ensures that cleared references are properly sent to the backend
    (column as any).mapping_references = columnMappingReferences;
  });

  return sortedColumns;
};
export default reverseParser;
